import { Injectable } from '@angular/core';
import { HistoryItemResponse } from '@shared/index';
import { dialogRecorderGetDialogSchema, dialogRecorderIdSchema } from '@shared/ragai.schemas';
import { merge, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { z } from 'zod';
import { OpenaiService } from './shared/services/ai/text/openai.service';
import { EventManagerService } from './shared/services/event-manager/event-manager.service';
import { KeyValService } from './shared/services/key-val.service';
import { MessageManagerService } from './shared/services/message-manager.service';
import { SocketService } from './shared/services/socket.service';

const KEYVAL_TYPE = 'DIALOGRECORDER';

/**
 * Zeichnet Nutzer-/Bot-Nachrichten mehrerer unabhängiger, benannter Dialoge auf (`RAGAI.DIALOGRECORDER`).
 * @remarks
 * Ein Recorder zeichnet nur auf, was auch im Chat sichtbar ist: nicht-Bypass-Nachrichten der Rollen `user`/`assistant`
 * mit nicht-leerem Text, so wie sie vom Server in den Thread geschrieben werden (nach `BEFORE_SEND`-Hooks).
 * Unsichtbare Prompts aus `RAGAI.MSG.prompt()`, Bypass-Nachrichten, Cards, Doc-Search- und MCP-Einträge fehlen bewusst.
 * Mehrere Recorder können parallel unter verschiedenen `id`s laufen (intern eine `Map`); jeder persistiert seinen
 * Zustand fortlaufend unter `RAGAI.KEYVAL`-Key `dialogrecorder_<id>` (Typ `DIALOGRECORDER`) und stellt ihn beim
 * nächsten {@link init} nach einem Seiten-Reload automatisch wieder her.
 */
@Injectable({ providedIn: 'root' })
export class DialogRecorderService {
  private _recorders = new Map<string, RecorderState>();

  /** @deprecated Nur für Abwärtskompatibilität mit alten Blöcken */
  public BOT_NAME = 'Bot';
  /** @deprecated Nur für Abwärtskompatibilität mit alten Blöcken */
  public USER_NAME = 'User';

  constructor(
    private MSG: MessageManagerService,
    private AITEXT_OPENAI: OpenaiService,
    private EVENTS: EventManagerService,
    private KEYVAL: KeyValService,
    private socketService: SocketService
  ) { }

  private _getOrCreate(id: string): RecorderState {
    if (!this._recorders.has(id)) {
      this._recorders.set(id, {
        isRecording: false,
        messages: [],
        subscription: undefined,
        recordedRunItemIds: new Set<string>()
      });
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this._recorders.get(id)!;
  }

  /** Legt einen neuen Recorder an, ohne die Aufzeichnung zu starten. */
  public async init(id: string | z.input<typeof dialogRecorderIdSchema>) {
    const parsed = dialogRecorderIdSchema.safeParse(id);
    if (parsed.success && typeof id === 'object') { id = parsed.data.recorderId; }
    const state = this._getOrCreate(id as string);
    // Nach einem Seiten-Reload gespeicherte Messages aus KEYVAL wiederherstellen
    if (state.messages.length === 0) {
      const stored = await this.KEYVAL.get('dialogrecorder_' + (id as string), KEYVAL_TYPE) as SimpleDialogMessage[] | null;
      if (Array.isArray(stored) && stored.length > 0) {
        state.messages = stored;
      }
    }
    this.EVENTS.trigger('RAGAI.DIALOGRECORDER.INITIALIZED', { id });
  }

  /** Startet die Aufzeichnung der im Chat sichtbaren Nachrichten und persistiert sie fortlaufend in KEYVAL. */
  public record(id: string | z.input<typeof dialogRecorderIdSchema> = 'default') {
    const parsed = dialogRecorderIdSchema.safeParse(id);
    if (parsed.success && typeof id === 'object') { id = parsed.data.recorderId; }
    const state = this._getOrCreate(id as string);
    if (state.isRecording) return;
    state.isRecording = true;

    const idStr = id as string;
    state.subscription = merge(
      this.socketService.onAddUserMessage.pipe(map(data => [data.message])),
      this.socketService.onCreateMessagesInThread.pipe(map(data => data.messages)),
      this.socketService.onRunComplete.pipe(map(message => [message]))
    ).subscribe(items => { this._append(state, idStr, items); });
  }

  /**
   * Übernimmt eine abgeschlossene Bot-Antwort sofort in alle laufenden Recorder.
   * @remarks Der Server sendet `idle` vor dem Socket-`runComplete`; ohne diesen Aufruf fehlt die Antwort in `RAGAI.MSG.runComplete`-Handlern.
   */
  public recordRunComplete(item: HistoryItemResponse) {
    for (const [id, state] of this._recorders) {
      if (state.isRecording) this._append(state, id, [item]);
    }
  }

  private _append(state: RecorderState, id: string, items: HistoryItemResponse[]) {
    const fresh = items.filter(item => {
      if (item.role !== 'assistant' || !item.item_id) return true;
      if (state.recordedRunItemIds.has(item.item_id)) return false;
      state.recordedRunItemIds.add(item.item_id);
      return true;
    });
    const visible = fresh.map(toVisibleDialogMessage).filter((m): m is SimpleDialogMessage => m !== null);
    if (visible.length === 0) return;
    state.messages.push(...visible);
    void this.KEYVAL.set('dialogrecorder_' + id, state.messages, KEYVAL_TYPE);
    for (const m of visible) {
      this.EVENTS.trigger('RAGAI.DIALOGRECORDER.RECORDED', { id, role: m.role, text: m.text });
    }
  }

  /** Beendet die Aufzeichnung, meldet die Event-Hooks ab und persistiert den finalen Nachrichtenstand. */
  public async stop(id: string | z.input<typeof dialogRecorderIdSchema> = 'default') {
    const parsed = dialogRecorderIdSchema.safeParse(id);
    if (parsed.success && typeof id === 'object') { id = parsed.data.recorderId; }
    const state = this._recorders.get(id as string);
    if (!state) return;
    state.isRecording = false;
    state.subscription?.unsubscribe();
    state.subscription = undefined;
    this.EVENTS.trigger('RAGAI.DIALOGRECORDER.STOPPED', { id, messages: state.messages });
    await this.KEYVAL.set('dialogrecorder_' + (id as string), state.messages, KEYVAL_TYPE);
  }

  /** Stoppt die Aufzeichnung und löscht alle bisher aufgezeichneten Nachrichten des Recorders. */
  public async reset(id: string | z.input<typeof dialogRecorderIdSchema> = 'default') {
    const parsed = dialogRecorderIdSchema.safeParse(id);
    if (parsed.success && typeof id === 'object') { id = parsed.data.recorderId; }
    await this.stop(id);
    const state = this._recorders.get(id as string);
    if (state) state.messages = [];
    this.EVENTS.trigger('RAGAI.DIALOGRECORDER.RESET', { id });
    await this.KEYVAL.set('dialogrecorder_' + (id as string), [], KEYVAL_TYPE);
  }

  private async _getMessages(id: string): Promise<SimpleDialogMessage[]> {
    if (this._recorders.has(id)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return this._recorders.get(id)!.messages;
    }
    // Nach Seiten-Reload aus KEYVAL wiederherstellen
    const stored = await this.KEYVAL.get('dialogrecorder_' + id, KEYVAL_TYPE) as SimpleDialogMessage[] | null;
    if (Array.isArray(stored) && stored.length > 0) {
      this._getOrCreate(id).messages = stored;
      return stored;
    }
    return [];
  }

  /** Gibt den aufgezeichneten Dialog als Klartext zurück, eine Zeile je Nachricht im Format `"Name: Text"`. */
  public async getDialogText(id: string | z.input<typeof dialogRecorderGetDialogSchema> = 'default', userName = 'User', botName = 'Bot'): Promise<string> {
    const parsed = dialogRecorderGetDialogSchema.safeParse(id);
    if (parsed.success && typeof id === 'object') {
      userName = parsed.data.userName ?? userName;
      botName = parsed.data.botName ?? botName;
      id = parsed.data.recorderId;
    }
    const messages = await this._getMessages(id as string);
    return messages.map(m =>
      m.role === 'user' ? `${userName}: ${m.text}` : `${botName}: ${m.text}`
    ).join('\n');
  }

  /** Wie getDialogText(), aber mit `<br />` statt Zeilenumbrüchen - direkt als HTML einbettbar. */
  public async getDialogHtml(id: string | z.input<typeof dialogRecorderGetDialogSchema> = 'default', userName = 'User', botName = 'Bot'): Promise<string> {
    const parsed = dialogRecorderGetDialogSchema.safeParse(id);
    if (parsed.success && typeof id === 'object') {
      userName = parsed.data.userName ?? userName;
      botName = parsed.data.botName ?? botName;
      id = parsed.data.recorderId;
    }
    return (await this.getDialogText(id, userName, botName)).replace(/\n/g, '<br />');
  }

  /** Gibt den aufgezeichneten Dialog als strukturiertes Array von `{role, text}`-Nachrichten zurück. */
  public async getDialogJson(id: string | z.input<typeof dialogRecorderIdSchema> = 'default'): Promise<SimpleDialogMessage[]> {
    const parsed = dialogRecorderIdSchema.safeParse(id);
    if (parsed.success && typeof id === 'object') { id = parsed.data.recorderId; }
    return this._getMessages(id as string);
  }

  /**
   * Legacy-Variante von getDialogText() für den Recorder "default" mit festen Sprecher-Namen.
   * @deprecated Verwende getDialogText(id, userName, botName)
   */
  public async getDialog(): Promise<string> {
    return this.getDialogText('default', this.USER_NAME, this.BOT_NAME);
  }
}

// Gleiche Sichtbarkeitsregel wie bot-chat-dialog.component.html, beschränkt auf echte Dialogbeiträge.
function toVisibleDialogMessage(item: HistoryItemResponse): SimpleDialogMessage | null {
  if (item.bypass) return null;
  if (item.role !== 'user' && item.role !== 'assistant') return null;
  const content = item.content.at(0);
  if (content?.type !== 'text' || content.text.trim() === '') return null;
  return { role: item.role === 'user' ? 'user' : 'bot', text: content.text };
}

type RecorderState = {
  isRecording: boolean;
  messages: SimpleDialogMessage[];
  subscription: Subscription | undefined;
  recordedRunItemIds: Set<string>;
};

export type SimpleDialogMessage = {
  role: 'user' | 'bot';
  text: string;
};
